import Phaser from "phaser";
import type { EntityState } from "odyssey-shared";
import { say } from "../../../words.ts";
import { layerOf } from "../../vfx/definition.ts";
import type { VfxFeed } from "../../vfx/feed.ts";
import { VfxPool } from "../../vfx/pool.ts";
import type { VfxRegistry } from "../../vfx/registry.ts";
import { vfxState } from "../../vfx/state.ts";
import { GOLD_TONES, tonesOf, type Tones } from "../../vfx/tone.ts";
import type { ReducedMotion } from "../../reduced-motion.ts";
import { emojiStackTop } from "../emoji/layout.ts";
import type { WorldUiModule } from "../hook.ts";
import { PIXEL_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { BURST_TEXTURE, PIXEL_FRAME, bakeAtlas } from "./art.ts";
import { bindSet, createSet, drawSet, hideSet, newDrawContext, type BurstSet, type DrawAt } from "./draw.ts";
import { EDGE_BANDS, artScaleOf, edgeAlpha, edgeRects, newEdgeRects } from "./geometry.ts";
import type { BurstBox, VfxReading, VfxReport } from "./report.ts";
import { burstSprite, hideSprite, putSprite, type BurstSprite } from "./sprite.ts";

/**
 * The vfx surface (ADR-134 decision 5): it draws every vfx the page plays, on the character each is played on. The player
 * (`vfx/player.ts`) queues requests in the page's feed and this surface drains them each frame, starting each in a slot of
 * a pool of `VFX_CAP` (`vfx/pool.ts`). A slot is a set of images drawn by the UI camera in the UI layer, under every plate
 * (depth below 0), from art drawn in code and baked into one nearest-filtered atlas (`art.ts`), at a whole number of device
 * pixels an art pixel (`geometry.ts`), placed from the world point the character's feet stand at, in the tones the play
 * asked for (`vfx/tone.ts`).
 *
 * A vfx shows only while its character is drawn and not hidden from the viewer (concealed and not targeted, ADR-061's rule
 * for a surface drawn for an entity); while hidden its time still runs; it ends when its time runs out or its character is
 * no longer drawn. Under reduced motion each vfx takes its definition's reduced form. The page's own character's vfx with an
 * edge glow layer also draws the glow at the screen's edges.
 *
 * A frame of steady play allocates nothing: the images of a slot are made the first time it is used, the characters are
 * visited through functions made once, and an image's frame, place, size, tint and strength are set only when they change.
 * With no vfx running no character is visited.
 */

/**
 * The depth the parts draw at in the UI layer, in the order they stack: under the plates at 0 and the bubbles at 1.
 */
const DEPTH = { edge: -3, ring: -1.5, pillarDark: -1.4, pillarGold: -1.39, pillarCore: -1.38, aura: -1.3, ray: -1.2, spark: -1.1, signBorder: -1.05, signFill: -1.04, text: -1.03 } as const;

/**
 * Adds the atlas to the scene's textures, once, nearest-filtered.
 *
 * @param scene The scene.
 * @throws Error when the canvas texture cannot be made.
 */
function ensureAtlas(scene: Phaser.Scene): void {
  if (scene.textures.exists(BURST_TEXTURE)) {
    return;
  }
  const atlas = bakeAtlas();
  const texture = scene.textures.createCanvas(BURST_TEXTURE, atlas.width, atlas.height);
  if (texture === null) {
    throw new Error("the vfx canvas texture could not be made");
  }
  texture.context.putImageData(new ImageData(new Uint8ClampedArray(atlas.pixels), atlas.width, atlas.height), 0, 0);
  for (const frame of atlas.frames) {
    texture.add(frame.name, 0, frame.x, frame.y, frame.width, frame.height);
  }
  texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
  texture.refresh();
}

/**
 * A sprite's box in logical pixels, or null while it is hidden.
 *
 * @param sprite The sprite.
 * @returns The box.
 */
function boxOf(sprite: BurstSprite): BurstBox | null {
  return sprite.shown ? { left: sprite.left / sprite.scale, top: sprite.top / sprite.scale, width: sprite.width / sprite.scale, height: sprite.height / sprite.scale } : null;
}

/**
 * The box of a vfx's whole pillar in logical pixels, from the stretch over the body and the stretch above it, or null
 * while neither is shown.
 *
 * @param set The slot.
 * @returns The box.
 */
function pillarBoxOf(set: BurstSet): BurstBox | null {
  const upper = boxOf(set.pillarDark);
  const body = boxOf(set.bodyDark);
  if (upper === null) {
    return body;
  }
  if (body === null) {
    return upper;
  }
  return { left: upper.left, top: upper.top, width: upper.width, height: body.top + body.height - upper.top };
}

/**
 * The vfx surface.
 *
 * @param report The game report's vfx entry: its switch and counts, and where the reader of the vfx goes.
 * @param feed The page's feed of vfx asked for.
 * @param registry The definitions the feed's indices name.
 * @param motion The game's reduced motion, read each frame.
 * @returns The surface's module.
 */
export function vfx(report: VfxReport, feed: VfxFeed, registry: VfxRegistry, motion: ReducedMotion): WorldUiModule {
  return {
    id: "vfx",
    /**
     * Bakes the atlas and builds the pool and the frame's visitors. No slot's images are made until it is first needed.
     *
     * @param context The UI layer, the projection and the characters the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      ensureAtlas(scene);
      /**
       * Makes a hidden sprite of the atlas in the UI layer.
       *
       * @param depth Its depth.
       * @returns The sprite.
       */
      const sprite = (depth: number): BurstSprite => burstSprite(context.add(new Phaser.GameObjects.Image(scene, 0, 0, BURST_TEXTURE, PIXEL_FRAME)).setOrigin(0, 0).setDepth(depth).setVisible(false));
      /**
       * Makes the images of one slot.
       *
       * @returns The slot's images.
       */
      const made = (): BurstSet => {
        const text = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("nameplate"))).setOrigin(0, 0).setDepth(DEPTH.text).setVisible(false);
        return createSet(
          {
            ring: () => sprite(DEPTH.ring),
            aura: () => sprite(DEPTH.aura),
            pillarDark: () => sprite(DEPTH.pillarDark),
            pillarGold: () => sprite(DEPTH.pillarGold),
            pillarCore: () => sprite(DEPTH.pillarCore),
            ray: () => sprite(DEPTH.ray),
            spark: () => sprite(DEPTH.spark),
            signBorder: () => sprite(DEPTH.signBorder),
            signFill: () => sprite(DEPTH.signFill),
          },
          text,
        );
      };
      const pool = new VfxPool<BurstSet>(made);
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const head = { x: 0, y: 0 };
      const at: DrawAt = { x: 0, y: 0, signStand: 0, body: 0 };
      const drawing = newDrawContext();
      const camera = context.worldCamera;
      const edge = newEdgeRects();
      const edgeSprites: BurstSprite[] = [];
      let frameNumber = 0;
      let now = 0;
      let reduced = false;
      let shown = 0;
      let ownStep = 0;
      let ownTones: Tones = GOLD_TONES;
      let atX = 0;
      let atY = 0;
      let atHeight = 0;
      let atHidden = false;

      /**
       * Starts a vfx on a character, or starts it again if it runs there, from the frame's time. A vfx asked for while the
       * switch is off, with an index the registry lacks or for a handle no snapshot carries, starts nothing.
       *
       * @param handle The character's handle.
       * @param key The vfx's index in the registry.
       * @param own Whether it is the page's own character.
       * @param tint The tint, or a negative number for none.
       * @param scale The reach: the multiple of the ring's radius and the sparks' spread.
       */
      const start = (handle: number, key: number, own: boolean, tint: number, scale: number): void => {
        const definition = registry.at(key);
        if (!report.enabled || definition === undefined || !Number.isInteger(handle) || handle < 0 || handle > 0xffff) {
          return;
        }
        const held = pool.start(handle, key, now);
        bindSet(held, definition, tonesOf(tint));
        const word = layerOf(definition, "text")?.word;
        held.text.setText(word === undefined ? "" : (say as (key: string) => string)(word));
        held.text.setTint(held.tones.light);
        held.scale = scale;
        held.startedAt = now;
        held.own = own;
        held.seen = frameNumber;
        held.over = false;
        report.started += 1;
      };

      /**
       * Brings one running vfx of the character being visited up to date for the frame.
       *
       * @param held The slot's images.
       */
      const visitSlot = (held: BurstSet): void => {
        const definition = held.def;
        if (definition === null) {
          return;
        }
        held.seen = frameNumber;
        held.reduced = reduced;
        held.elapsed = now - held.startedAt;
        if (!vfxState(definition, held.elapsed, reduced, held.state)) {
          held.over = true;
          hideSet(held);
          return;
        }
        if (held.own && held.state.edgeStep > 0) {
          ownStep = Math.max(ownStep, held.state.edgeStep);
          ownTones = held.tones;
        }
        if (atHidden) {
          hideSet(held);
          return;
        }
        const scale = context.scale.current;
        drawing.scale = scale;
        drawing.k = artScaleOf(camera.zoom);
        drawing.reach = held.scale;
        context.project(atX, atY, anchor);
        context.project(atX, atY - atHeight, head);
        at.x = Math.round(anchor.x * scale);
        at.y = Math.round(anchor.y * scale);
        at.body = Math.round(atHeight) * drawing.k + drawing.k;
        at.signStand = emojiStackTop(Math.round(head.y * scale), scale, true, false);
        if (at.x < -60 * drawing.k || at.x > camera.width + 60 * drawing.k || at.y < 0 || at.y > camera.height + 60 * drawing.k) {
          hideSet(held);
          return;
        }
        drawSet(held, at, drawing);
        shown += 1;
      };

      /**
       * Brings the vfx of one drawn character up to date for the frame.
       *
       * @param handle The character's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param _state Its state.
       * @param height How far its art reaches above its feet, in world pixels.
       * @param hidden Whether its world UI hides: its vfx are kept hidden with their time still running.
       */
      const visit = (handle: number, x: number, y: number, _state: EntityState, height: number, hidden: boolean): void => {
        atX = x;
        atY = y;
        atHeight = height;
        atHidden = hidden;
        pool.forEachOf(handle, visitSlot);
      };

      /**
       * Ends a vfx whose time ran out or whose character was not drawn on the frame.
       *
       * @param held The slot's images.
       * @param _handle Its character's handle.
       * @param _key The vfx's index.
       * @param _startedAt When it started.
       * @param slot Its slot.
       */
      const sweep = (held: BurstSet, _handle: number, _key: number, _startedAt: number, slot: number): void => {
        if (held.over || held.seen !== frameNumber) {
          hideSet(held);
          pool.end(slot);
        }
      };

      /**
       * Draws the page's own edge glow: three nested bands at the screen's edges, stepping down in quarters.
       */
      const drawEdge = (): void => {
        if (ownStep <= 0) {
          for (const piece of edgeSprites) {
            hideSprite(piece);
          }
          return;
        }
        if (edgeSprites.length === 0) {
          for (let index = 0; index < EDGE_BANDS * 4; index += 1) {
            edgeSprites.push(sprite(DEPTH.edge));
          }
        }
        const scale = context.scale.current;
        edgeRects(camera.width, camera.height, artScaleOf(camera.zoom), edge);
        for (let index = 0; index < edge.length; index += 1) {
          const rect = edge[index] as (typeof edge)[number];
          const alpha = edgeAlpha(ownStep, rect.band);
          const piece = edgeSprites[index] as BurstSprite;
          if (alpha <= 0 || rect.width <= 0 || rect.height <= 0) {
            hideSprite(piece);
          } else {
            putSprite(piece, PIXEL_FRAME, rect.left, rect.top, rect.width, rect.height, scale, ownTones.mid, alpha);
          }
        }
      };

      /**
       * Reads every vfx running, for the report: made on request, never on a frame.
       *
       * @returns One reading each.
       */
      report.list = (): readonly VfxReading[] => {
        const readings: VfxReading[] = [];
        pool.forEachActive((held, handle) => {
          readings.push({ id: held.def?.id ?? "", handle, own: held.own, elapsed: held.elapsed, shown: held.pillarDark.shown || held.bodyDark.shown || held.ring.shown || held.signBorder.shown || held.aura.shown, pillar: pillarBoxOf(held), ring: boxOf(held.ring), sign: boxOf(held.signBorder), aura: boxOf(held.aura), rays: held.raysShown, sparks: held.sparksShown, reduced: held.reduced, tint: held.tones.mid });
        });
        return readings;
      };

      return {
        /**
         * Takes the vfx asked for since the last frame, visits every character drawn this frame while any vfx runs, ends
         * those no visit saw or whose time ran out, and draws the edge glow. With the switch off every vfx ends.
         *
         * @param time The scene's time in ms, which a vfx's life is counted on.
         */
        update(time) {
          frameNumber += 1;
          now = time;
          reduced = motion.reduced();
          shown = 0;
          ownStep = 0;
          feed.take(start);
          if (report.enabled && pool.active > 0 && context.characters.local(local)) {
            context.characters.forEach(visit);
          }
          pool.forEachActive(sweep);
          drawEdge();
          report.active = pool.active;
          report.created = pool.created;
          report.shown = shown;
          report.edgeStep = ownStep;
        },
        /**
         * Forgets the report's reader, which would hold the destroyed images.
         */
        destroy() {
          /**
           * Lists no vfx, since none runs once the scene has shut down.
           *
           * @returns No readings.
           */
          report.list = (): readonly VfxReading[] => [];
        },
      };
    },
  };
}
